/**
 * Lop goi /admin/* cho dashboard.
 *
 * Ba viec o day, va chi ba viec:
 *   1. Luu / doc thong tin xac thuc trong localStorage.
 *   2. Gan dung header cho tung kieu xac thuc (Bearer hay X-Admin-Token).
 *   3. Bien loi HTTP thanh mot loai loi duy nhat (ApiError) de trang goi khong phai
 *      doan xem res.ok, res.status hay body loi co dang gi.
 *
 * 401/403 duoc xu ly tap trung: xoa key dang luu roi bao cho App quay ve man hinh dan key.
 * Neu de tung trang tu xu ly thi se co trang quen, va nguoi dung ket o mot man hinh trong.
 */

import { STATS_RANGES } from '@capforge/shared';
import type {
  AdminErrorResponse,
  ApiKeyDto,
  AuditLogDto,
  CreateApiKeyResponse,
  Paginated,
  ProfileDto,
  SampleDto,
  SolverInfo,
  StatsResponse,
  TaskDto,
  UpstreamSettingsDto,
} from '@capforge/shared';

export type StatsRange = (typeof STATS_RANGES)[number];

// ---------------------------------------------------------------------------
// Xac thuc
// ---------------------------------------------------------------------------

/**
 * Hai duong vao admin ma server chap nhan:
 *   - `bearer`: API key co scope admin, gui qua Authorization: Bearer <key>
 *   - `adminToken`: CAPFORGE_ADMIN_TOKEN trong .env, gui qua header X-Admin-Token
 * Nguoi dung tu chon vi hai chuoi nay nhin ben ngoai khong phan biet duoc.
 */
export const AUTH_MODES = ['bearer', 'adminToken'] as const;
export type AuthMode = (typeof AUTH_MODES)[number];

export interface AdminAuth {
  key: string;
  mode: AuthMode;
}

/** Ten khoa do dac ta quy dinh — dung sai la dashboard va tai lieu lech nhau. */
const STORAGE_KEY = 'capforge.adminKey';
const STORAGE_MODE = 'capforge.adminKeyMode';

/** localStorage co the nem loi (che do rieng tu, cookie bi chan) — khong duoc lam sap UI. */
function readStorage(name: string): string | null {
  try {
    return window.localStorage.getItem(name);
  } catch {
    return null;
  }
}

function writeStorage(name: string, value: string | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(name);
    } else {
      window.localStorage.setItem(name, value);
    }
  } catch {
    // Khong luu duoc thi phien nay van chay duoc bang bien trong bo nho.
  }
}

function isAuthMode(value: string | null): value is AuthMode {
  return value === 'bearer' || value === 'adminToken';
}

/** Ban sao trong bo nho de van chay duoc khi localStorage bi chan. */
let currentAuth: AdminAuth | null = null;
let authNotice: string | null = null;
let bootstrapped = false;

function bootstrap(): void {
  if (bootstrapped) return;
  bootstrapped = true;
  const key = readStorage(STORAGE_KEY);
  if (key === null || key.length === 0) return;
  const mode = readStorage(STORAGE_MODE);
  currentAuth = { key, mode: isAuthMode(mode) ? mode : 'bearer' };
}

type AuthListener = (auth: AdminAuth | null) => void;
const authListeners = new Set<AuthListener>();

function emitAuth(): void {
  for (const listener of authListeners) {
    listener(currentAuth);
  }
}

export function getAuth(): AdminAuth | null {
  bootstrap();
  return currentAuth;
}

export function setAuth(auth: AdminAuth): void {
  bootstrap();
  currentAuth = auth;
  authNotice = null;
  writeStorage(STORAGE_KEY, auth.key);
  writeStorage(STORAGE_MODE, auth.mode);
  emitAuth();
}

/** `reason` se duoc hien o man hinh dan key de nguoi dung biet vi sao bi day ra. */
export function clearAuth(reason?: string): void {
  bootstrap();
  currentAuth = null;
  authNotice = reason ?? null;
  writeStorage(STORAGE_KEY, null);
  writeStorage(STORAGE_MODE, null);
  emitAuth();
}

export function getAuthNotice(): string | null {
  return authNotice;
}

/** Tra ve ham huy dang ky — goi trong cleanup cua useEffect. */
export function onAuthChange(listener: AuthListener): () => void {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
}

function buildAuthHeaders(auth: AdminAuth): Record<string, string> {
  return auth.mode === 'bearer'
    ? { Authorization: `Bearer ${auth.key}` }
    : { 'X-Admin-Token': auth.key };
}

// ---------------------------------------------------------------------------
// Loi
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Bien mot exception bat ky thanh chuoi hien duoc — khong bao gio tra "[object Object]". */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Lỗi không xác định';
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

/**
 * Cho phep tro dashboard sang mot server khac khi build (vi du dashboard dat tren CDN).
 * Bo trong thi dung duong dan tuong doi — dev thi vite proxy, production thi server
 * tu phuc vu file tinh nen cung goc.
 */
const rawBase: unknown = import.meta.env['VITE_CAPFORGE_API_BASE'];
const API_BASE = typeof rawBase === 'string' ? rawBase.replace(/\/+$/, '') : '';

function isAdminErrorResponse(value: unknown): value is AdminErrorResponse {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return typeof obj['error'] === 'string' && typeof obj['message'] === 'string';
}

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // Server tra HTML (proxy sai duong dan, server chua chay) — giu nguyen van ban de bao loi.
    return { __raw: text };
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Object se duoc JSON.stringify. */
  body?: unknown;
  /** Dung khi kiem tra key o man hinh dang nhap, luc key chua duoc luu. */
  auth?: AdminAuth;
  signal?: AbortSignal;
}

export async function adminFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const auth = options.auth ?? getAuth();
  if (auth === null) {
    throw new ApiError(401, 'unauthorized', 'Chưa dán admin key');
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...buildAuthHeaders(auth),
  };
  let payload: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(options.body);
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: payload,
      signal: options.signal,
    });
  } catch (err) {
    // Mat mang / server chua chay: noi thang ra thay vi de "Failed to fetch" kho hieu.
    throw new ApiError(0, 'network_error', `Không kết nối được tới server: ${errorMessage(err)}`);
  }

  const data = await parseJson(res);

  if (res.status === 401 || res.status === 403) {
    // Chi day nguoi dung ra khi key dang luu chinh la key bi tu choi. Neu dang thu
    // mot key moi o man hinh dang nhap thi khong duoc dong phien hien tai.
    if (options.auth === undefined) {
      clearAuth('Key bị từ chối. Hãy dán lại admin key hoặc admin token.');
    }
    const message = isAdminErrorResponse(data)
      ? data.message
      : 'Key không hợp lệ hoặc không có quyền admin';
    throw new ApiError(res.status, 'unauthorized', message, data);
  }

  if (!res.ok) {
    if (isAdminErrorResponse(data)) {
      throw new ApiError(res.status, data.error, data.message, data.details);
    }
    throw new ApiError(res.status, 'http_error', `Server tra ma ${res.status}`, data);
  }

  return data as T;
}

/** Xay query string, bo qua gia tri rong de URL khong day nhung tham so vo nghia. */
function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    const text = String(value);
    if (text.length === 0) continue;
    search.set(key, text);
  }
  const out = search.toString();
  return out.length > 0 ? `?${out}` : '';
}

export interface TaskListParams {
  limit?: number;
  cursor?: number | null;
  status?: string;
  profileKey?: string;
  apiKey?: number | null;
}

export interface CreateApiKeyBody {
  label: string;
  scopes: string[];
  rateLimitPerMin: number;
  dailyQuota: number | null;
}

export interface UpdateApiKeyBody {
  label?: string;
  scopes?: string[];
  rateLimitPerMin?: number;
  dailyQuota?: number | null;
  enabled?: boolean;
}

export const api = {
  /** Goi mot endpoint re tien de xac nhan key dung, truoc khi luu vao localStorage. */
  verify(auth: AdminAuth): Promise<SolverInfo[]> {
    return adminFetch<SolverInfo[]>('/admin/solvers', { auth });
  },

  stats(range: StatsRange, apiKeyId?: number | null, signal?: AbortSignal): Promise<StatsResponse> {
    return adminFetch<StatsResponse>(`/admin/stats${qs({ range, apiKey: apiKeyId })}`, { signal });
  },

  keys(signal?: AbortSignal): Promise<ApiKeyDto[]> {
    return adminFetch<ApiKeyDto[]>('/admin/keys', { signal });
  },

  createKey(body: CreateApiKeyBody): Promise<CreateApiKeyResponse> {
    return adminFetch<CreateApiKeyResponse>('/admin/keys', { method: 'POST', body });
  },

  updateKey(id: number, body: UpdateApiKeyBody): Promise<ApiKeyDto> {
    return adminFetch<ApiKeyDto>(`/admin/keys/${id}`, { method: 'PATCH', body });
  },

  deleteKey(id: number): Promise<{ ok: boolean }> {
    return adminFetch<{ ok: boolean }>(`/admin/keys/${id}`, { method: 'DELETE' });
  },

  tasks(params: TaskListParams, signal?: AbortSignal): Promise<Paginated<TaskDto>> {
    return adminFetch<Paginated<TaskDto>>(
      `/admin/tasks${qs({
        limit: params.limit,
        cursor: params.cursor,
        status: params.status,
        profileKey: params.profileKey,
        apiKey: params.apiKey,
      })}`,
      { signal },
    );
  },

  task(id: number, signal?: AbortSignal): Promise<TaskDto> {
    return adminFetch<TaskDto>(`/admin/tasks/${id}`, { signal });
  },

  profiles(signal?: AbortSignal): Promise<ProfileDto[]> {
    return adminFetch<ProfileDto[]>('/admin/profiles', { signal });
  },

  solvers(signal?: AbortSignal): Promise<SolverInfo[]> {
    return adminFetch<SolverInfo[]>('/admin/solvers', { signal });
  },

  upstream(signal?: AbortSignal): Promise<UpstreamSettingsDto> {
    return adminFetch<UpstreamSettingsDto>('/admin/upstream', { signal });
  },

  updateUpstream(body: Record<string, unknown>): Promise<UpstreamSettingsDto> {
    return adminFetch<UpstreamSettingsDto>('/admin/upstream', { method: 'PATCH', body });
  },

  auditLog(limit: number, signal?: AbortSignal): Promise<AuditLogDto[]> {
    return adminFetch<AuditLogDto[]>(`/admin/audit?limit=${limit}`, { signal });
  },

  /**
   * Mo mot ket noi log truc tiep toi /admin/events.
   *
   * ==========================================================================
   *  VI SAO KHONG DUNG `EventSource` — API co san cua trinh duyet cho dung viec nay.
   *
   *  `EventSource` KHONG gui duoc header tuy y, ma toan bo /admin/* chi xac thuc bang
   *  header (Authorization hoac X-Admin-Token). Hai duong lui deu dat hon:
   *    - Nhet key vao query string: du an da co quyet dinh thanh van chong lai dieu do
   *      (xem lib/useSampleImage.ts). Key trong URL se nam trong access log cua reverse
   *      proxy dat truoc va trong lich su trinh duyet.
   *    - Them mot duong xac thuc THU BA (endpoint cap ticket ngan han): them mot be mat
   *      xac thuc nua chi de tiet kiem vai chuc dong ma.
   *
   *  `fetch` + `ReadableStream` gui duoc header binh thuong. Cai gia phai tra la tu
   *  viet lai phan tach frame va ket noi lai — chinh la doan duoi day.
   * ==========================================================================
   *
   * @param onLine  goi voi tung dong log (chuoi JSON tho tu pino)
   * @param onNotice goi khi co su kien khong phai log (vd server bao da bo bot dong)
   * @returns ham dong ket noi
   */
  streamEvents(
    onLine: (line: string) => void,
    onNotice: (text: string) => void,
    onError: (message: string) => void,
  ): () => void {
    const controller = new AbortController();
    let stopped = false;
    let retryMs = 1000;

    const run = async (): Promise<void> => {
      while (!stopped) {
        try {
          const auth = getAuth();
          if (auth === null) {
            onError('Chưa đăng nhập');
            return;
          }
          const res = await fetch(`${API_BASE}/admin/events`, {
            headers: buildAuthHeaders(auth),
            signal: controller.signal,
          });

          if (res.status === 401 || res.status === 403) {
            clearAuth('Key bị từ chối khi mở log trực tiếp.');
            return;
          }
          if (!res.ok || res.body === null) {
            // 503 = vuot tran so ket noi. Thong diep cua server noi ro phai lam gi,
            // nen dua nguyen van ra thay vi thay bang mot cau chung chung.
            let msg = `Server từ chối mở log trực tiếp (HTTP ${res.status})`;
            try {
              const body = (await res.json()) as { message?: string };
              if (typeof body.message === 'string') msg = body.message;
            } catch {
              /* body khong phai JSON — giu thong diep mac dinh */
            }
            onError(msg);
            return;
          }

          retryMs = 1000; // ket noi duoc roi thi dat lai thoi gian cho
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buf = '';

          for (;;) {
            const { done, value } = await reader.read();
            if (done || stopped) break;
            buf += decoder.decode(value, { stream: true });

            // SSE tach frame bang mot dong trong.
            let i = buf.indexOf('\n\n');
            while (i !== -1) {
              const frame = buf.slice(0, i);
              buf = buf.slice(i + 2);
              if (frame.startsWith('data: ')) {
                const payload = frame.slice(6);
                // Server gui mot su kien co kieu khi phai bo bot dong vi client cham.
                // Noi that con hon de man hinh thieu dong ma trong nhu day du.
                if (payload.includes('"kind":"dropped"')) {
                  try {
                    const o = JSON.parse(payload) as { count?: number };
                    onNotice(`Bỏ qua ${o.count ?? '?'} dòng vì trình duyệt không theo kịp`);
                  } catch {
                    /* bo qua */
                  }
                } else {
                  onLine(payload);
                }
              }
              i = buf.indexOf('\n\n');
            }
          }
        } catch (err) {
          if (stopped || controller.signal.aborted) return;
          onError(`Mất kết nối log: ${errorMessage(err)}`);
        }

        if (stopped) return;
        // Cho tang dan, tran 30 giay: server vua restart thi khong nen bi dam.
        await new Promise((r) => setTimeout(r, retryMs));
        retryMs = Math.min(retryMs * 2, 30_000);
      }
    };

    void run();
    return () => {
      stopped = true;
      controller.abort();
    };
  },

  // --- Phase 3: gan nhan, tu dien pHash, triage --------------------------

  samples(params: SampleListParams, signal?: AbortSignal): Promise<Paginated<SampleDto>> {
    return adminFetch<Paginated<SampleDto>>(
      `/admin/samples${qs({
        limit: params.limit,
        cursor: params.cursor,
        type: params.type,
        profile: params.profile,
        verified: params.verified,
      })}`,
      { signal },
    );
  },

  labelSample(id: number, body: LabelSampleBody): Promise<SampleDto> {
    return adminFetch<SampleDto>(`/admin/samples/${id}/label`, { method: 'POST', body });
  },

  dictionary(params: DictionaryParams, signal?: AbortSignal): Promise<DictionaryPage> {
    return adminFetch<DictionaryPage>(
      `/admin/dictionary${qs({
        profile: params.profile,
        unlabeledOnly: params.unlabeledOnly,
        limit: params.limit,
        offset: params.offset,
      })}`,
      { signal },
    );
  },

  upsertDictionaryEntry(body: {
    profileKey: string;
    phash: string;
    label: string;
  }): Promise<DictionaryEntryDto> {
    return adminFetch<DictionaryEntryDto>('/admin/dictionary', { method: 'POST', body });
  },

  deleteDictionaryEntry(id: number): Promise<{ ok: boolean }> {
    return adminFetch<{ ok: boolean }>(`/admin/dictionary/${id}`, { method: 'DELETE' });
  },

  rebuildDictionary(profileKey: string): Promise<RebuildResultDto> {
    return adminFetch<RebuildResultDto>('/admin/dictionary/rebuild', {
      method: 'POST',
      body: { profileKey },
    });
  },

  unknowns(signal?: AbortSignal): Promise<UnknownsResponse> {
    return adminFetch<UnknownsResponse>('/admin/unknowns', { signal });
  },
};

// ---------------------------------------------------------------------------
// Kieu du lieu cua Phase 3
// ---------------------------------------------------------------------------

export interface SampleListParams {
  limit?: number;
  cursor?: number | null;
  type?: string;
  profile?: string;
  /** true = chi mau da kiem chung; false = chi mau chua. Bo trong = ca hai. */
  verified?: boolean;
}

export interface LabelSampleBody {
  label: string;
  verified?: boolean;
  rejected?: boolean;
}

export interface DictionaryParams {
  profile: string;
  unlabeledOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface DictionaryEntryDto {
  id: number;
  profileKey: string;
  phash: string;
  label: string;
  hits: number;
  misses: number;
}

export interface DictionaryPage {
  items: DictionaryEntryDto[];
  total: number;
  missRate: number;
  lookupTotal: number;
  /** true khi ty le truot vuot nguong — dau hieu site da doi kho anh (muc 9). */
  alarm: boolean;
}

export interface RebuildResultDto {
  profileKey: string;
  scanned: number;
  created: number;
  updated: number;
  skippedInconsistent: number;
  skippedTooFew: number;
}

export interface UnknownGroupDto {
  phash: string;
  count: number;
  width: number | null;
  height: number | null;
  taskType: string;
  sampleIds: number[];
  similarProfile: { key: string; label: string; distance: number } | null;
}

export interface UnknownsResponse {
  groups: UnknownGroupDto[];
  scanned: number;
  scanLimit: number;
}
